import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Target, CheckCircle, XCircle } from 'lucide-react';
export function EmotionalRegulationPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-467">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-468">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-469">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-470">
            <span className="w-8 h-px bg-teal" data-id="element-471"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-472">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-473"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-474">
            Emotional Regulation
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-475">
            At SyNeural, emotional regulation refers to the ability to remain
            stable enough under stress, frustration, pressure, or conflict to
            think clearly and act responsibly. It does not mean suppressing
            emotion or becoming detached. It means being able to experience
            emotion without being ruled by it, and to recover from intensity
            without allowing it to distort judgment, relationships, or
            performance.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-476">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-477">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-478">
            Emotional regulation matters because reasoning, planning,
            cooperation, and leadership all deteriorate when people become
            excessively reactive, impulsive, or destabilized. Even strong
            intelligence or technical competence can be undermined when a person
            cannot maintain perspective under pressure. Emotional regulation is
            therefore one of the core foundations of readiness across personal,
            civic, institutional, and leadership settings.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-479">
        <div className="max-w-4xl mx-auto" data-id="element-480">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-481">
            <div className="flex items-center gap-4 mb-6" data-id="element-482">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-483">
                <Target size={24} data-id="element-484" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-485">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-486">
              Emotional regulation affects how people respond to challenge,
              disagreement, uncertainty, and responsibility. Weak regulation can
              lead to rash decisions, conflict escalation, burnout, fear-driven
              thinking, and poor judgment under pressure. Strong regulation
              supports steadier decision-making, healthier relationships, better
              teamwork, and greater reliability in high-stress environments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Emotional Regulation Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-487">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-488">
          <div className="flex items-center gap-4 mb-6" data-id="element-489">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-490">
              <CheckCircle size={24} data-id="element-491" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-492">
              What Strong Emotional Regulation Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-493">
            People with stronger emotional regulation can feel frustration,
            stress, anger, or fear without immediately acting on those feelings
            in destructive ways. They are better able to pause, recover,
            reframe, and respond with greater control. They do not necessarily
            feel less deeply, but they can stay functional and responsible when
            pressure rises.
          </p>
        </motion.div>
      </section>

      {/* What Weak Emotional Regulation Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-494">
        <div className="max-w-4xl mx-auto" data-id="element-495">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-496">
            <div className="flex items-center gap-4 mb-6" data-id="element-497">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-498">
                <XCircle size={24} data-id="element-499" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-500">
                What Weak Emotional Regulation Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-501">
              Weak emotional regulation often appears as impulsive reactions,
              rage-driven communication, panic responses, chronic overwhelm,
              inability to de-escalate, and poor stress tolerance. In personal
              life this can damage relationships and consistency. In
              institutions and leadership contexts it can create instability,
              mistrust, and repeated decision failures under pressure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-502">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-503">
          <div className="flex items-center gap-4 mb-6" data-id="element-504">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-505">
              <Heart size={24} data-id="element-506" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-507">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-508">
            Emotional regulation can be improved through stress-management
            practices, sleep and recovery support, behavioral coaching,
            mindfulness, reflection, physical health, conflict-resolution
            training, and better awareness of triggers and patterns. In the
            SyNeural framework, it is treated as a key developmental capability
            because clear thinking becomes much harder when emotional volatility
            remains unmanaged.
          </p>
        </motion.div>
      </section>
    </div>;
}